import { PalmettoIcon, PalmettoTextInput } from '@palmetto/dsm-components';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  type ChecklistItemInputDefinitionView,
  type ChecklistPendingFlagDecision,
  type RichTextNode,
  ChecklistItemInputType,
  FlagPurposeType,
  getEvaluationCore,
} from 'types';
import type { EvaluationParams, EvaluationState } from 'types';

// Single source of truth for decision-type fill styling, shared with the open-dropdown
// option chips and the flag-creation flow. Uses semantic DSM tag tokens so the trigger
// fill resolves correctly in BOTH light and dark mode (the previous bespoke
// `bg-banner-btn-*` classes were not real tokens — they emitted no CSS, leaving the
// trigger with no background/border and only inherited text, which read as white-on-
// nothing in dark mode).
import { flagPurposeTypeStyles } from './ChecklistFlagCreationFlow';
import { useChecklistTooltip } from './ChecklistTooltipDock';
import { evaluationClientRegistry } from './evaluationClientRegistry';

// Stable empty array references used as ?? fallbacks to avoid recreating arrays on every render.
const EMPTY_FLAG_VALUES: string[] = [];
const EMPTY_ACTIVE_FLAGS: ActiveRequirementFlag[] = [];
const EMPTY_REQUIREMENT_OPTIONS: NonNullable<ChecklistItemInputDefinitionView['options']> = [];
const EMPTY_EVALUATION_PARAMS: EvaluationParams = {};

// An option produces its own Flag when selected only if its decisionType is a real
// FlagPurposeType member. Options with no decisionType, or 'approved'/'approvedNoFlag',
// are terminal — a single, exclusive, non-flag decision stored directly in `data`
// (FIN-4866: a requirement input now supports selecting several flag-producing options
// alongside — never simultaneously with — one terminal decision).
const FLAG_PRODUCING_TYPES: readonly string[] = Object.values(FlagPurposeType);
const isFlagProducingOption = (option: { decisionType?: string }): boolean =>
  !!option.decisionType && FLAG_PRODUCING_TYPES.includes(option.decisionType);

/** A persisted, currently-open Flag record backing one selected option on a requirement input. */
export interface ActiveRequirementFlag {
  value: string;
  flagId: string;
}

export interface ChecklistItemInputsProps {
  inputs?: ChecklistItemInputDefinitionView[];
  data: Record<string, string | boolean | ChecklistPendingFlagDecision[]>;
  onChange: (data: Record<string, string | boolean>) => void;
  // Evaluation inputs report structured state, not the scalar `data` bag.
  onEvaluationChange?: (inputKey: string, state: EvaluationState) => void;
  readOnly: boolean;
  // Forwarded to evaluation inputs that call a server endpoint on demand and need to identify their item.
  itemKey?: string;
  // Persisted, currently-open flags for requirement inputs, keyed by input key and read
  // back from queue.flags by the caller. Never duplicated into `data`.
  activeFlagsByInput?: Record<string, ActiveRequirementFlag[]>;
  // Fires immediately (no confirmation) when the tech removes an already-persisted flag
  // chip. Only meaningful — and only wired through — while the input is editable.
  onRemoveActiveFlag?: (inputKey: string, flagId: string) => void;
  // Reports the full, up-to-date set of locally-toggled (unsaved) flag-producing option
  // values for an input every time it changes. `selectedFlagValuesByInput` above is the
  // source of truth for this component's own rendering; this is purely a notification
  // channel so the caller (ChecklistItemCard) can mirror the same state to compute
  // `flagDecisions`/required-filled logic without this component becoming controlled.
  onFlagSelectionsChange?: (inputKey: string, values: string[]) => void;
  // Fires the full ChecklistPendingFlagDecision[] for an input the moment its locally-toggled
  // selections change — the caller (ChecklistItemCard) debounces this into a draft PATCH so a
  // rejection/stipulation selection survives navigation before the flag wizard is ever opened.
  onPendingFlagSelectionsChange?: (inputKey: string, decisions: ChecklistPendingFlagDecision[]) => void;
  // Signals "flush whatever draft changes are pending now" — fired on a text input's blur and
  // on a RequirementSelect's close (dropdown closing, or a chip removed while already closed),
  // rather than on every keystroke/toggle. The caller (ChecklistItemCard) owns what's pending;
  // this is purely a "now" trigger, no payload.
  onDraftFlush?: () => void;
}

interface InputTooltipTriggerProps {
  tooltipId: string;
  tooltipText: RichTextNode[];
}

/** Mirrors the item-level guidance icon in ChecklistItemCard, scoped to one input. */
const InputTooltipTrigger: React.FC<InputTooltipTriggerProps> = ({ tooltipId, tooltipText }) => {
  const { activeId, openTooltip, closeTooltip } = useChecklistTooltip();
  const isOpen = activeId === tooltipId;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isOpen) {
        closeTooltip();
      } else {
        openTooltip(tooltipId, tooltipText);
      }
    },
    [isOpen, closeTooltip, openTooltip, tooltipId, tooltipText],
  );

  return (
    <div className="inline-flex shrink-0">
      <button
        type="button"
        aria-label="Open guidance"
        onClick={handleClick}
        className="text-text-tertiary hover:text-text-primary"
      >
        <PalmettoIcon name="infoSquare" size="sm" />
      </button>
    </div>
  );
};

interface InputWithTooltipSlotProps {
  tooltipTrigger?: React.ReactNode;
  anchorTop?: boolean;
  children: React.ReactNode;
}

// Always reserves the icon column, even with no tooltip, so inputs keep the same width
// whether or not a tooltip is configured and the icon never overlaps the field.
const InputWithTooltipSlot: React.FC<InputWithTooltipSlotProps> = ({ tooltipTrigger, anchorTop, children }) => (
  <div className={`flex gap-2 ${anchorTop ? 'items-start' : 'items-center'}`}>
    <div className="min-w-0 flex-1">{children}</div>
    <div className={`flex w-16 shrink-0 justify-center pl-10 ${anchorTop ? 'pt-28' : ''}`}>{tooltipTrigger}</div>
  </div>
);

interface RequirementSelectProps {
  inputKey: string;
  label: string;
  options: ChecklistItemInputDefinitionView['options'];
  terminalValue: string;
  selectedFlagValues: string[];
  activeFlags: ActiveRequirementFlag[];
  readOnly: boolean;
  onToggleFlagOption: (key: string, value: string) => void;
  onSelectTerminal: (key: string, value: string) => void;
  onRemoveActiveFlag?: (key: string, flagId: string) => void;
  // Fired when the dropdown closes (terminal selection or outside click), or when a
  // locally-toggled chip is deselected while already closed — the moment this input's
  // pending selection is "settled" and should be persisted, rather than on every toggle.
  onDraftFlush?: () => void;
  // Pre-built guidance trigger/popover for this input's label row, or undefined when the
  // input has no tooltipText. Rendered as-is — RequirementSelect doesn't own the open/close state.
  tooltipTrigger?: React.ReactNode;
}

const RequirementSelect: React.FC<RequirementSelectProps> = ({
  inputKey,
  label,
  options = EMPTY_REQUIREMENT_OPTIONS,
  terminalValue,
  selectedFlagValues,
  activeFlags,
  readOnly,
  onToggleFlagOption,
  onSelectTerminal,
  onRemoveActiveFlag,
  onDraftFlush,
  tooltipTrigger,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const [clipTop, setClipTop] = useState(0);
  // Captured from the trigger's themed context so the portaled dropdown (rendered to
  // document.body) can re-create the [data-mode]/[data-theme] scope DSM tokens need.
  const [portalTheme, setPortalTheme] = useState<{ mode: string; theme: string }>({
    mode: 'light',
    theme: 'core',
  });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const terminalOption = options.find((o) => o.value === terminalValue);

  // Chips rendered on the trigger: persisted active flags first, then locally-toggled
  // (unsaved) selections. Mutually exclusive with `terminalOption` by construction — the
  // parent clears `selectedFlagValues` whenever a terminal option is chosen.
  const activeValueSet = new Set(activeFlags.map((flag) => flag.value));
  const chips = [
    ...activeFlags.map((flag) => ({
      key: `active-${flag.flagId}`,
      value: flag.value,
      flagId: flag.flagId as string | undefined,
      persisted: true,
      option: options.find((o) => o.value === flag.value),
    })),
    ...selectedFlagValues
      .filter((value) => !activeValueSet.has(value))
      .map((value) => ({
        key: `local-${value}`,
        value,
        flagId: undefined as string | undefined,
        persisted: false,
        option: options.find((o) => o.value === value),
      })),
  ];
  // A terminal value always wins over stale chips: once a terminal option is picked, the
  // trigger must show it immediately, even before an active-flag removal call (fired by the
  // caller below) round-trips and `activeFlags` catches up.
  const hasChips = chips.length > 0 && !terminalValue;

  const triggerStyle = terminalOption
    ? flagPurposeTypeStyles[terminalOption.decisionType ?? 'approved']
    : 'border-2 border-[var(--text-info)] !text-[var(--text-info)] bg-transparent';

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onDraftFlush?.();
  }, [onDraftFlush]);

  const updateDropdownPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const maxHeight = 320;
    const minWidth = 180;

    // Find nearest scrollable ancestor.
    let scrollContainerTop = 0;
    let sc: HTMLElement | null = trigger.parentElement;
    while (sc) {
      const cs = window.getComputedStyle(sc);
      if (/(auto|scroll)/.test(cs.overflow) || /(auto|scroll)/.test(cs.overflowY)) {
        scrollContainerTop = sc.getBoundingClientRect().top;
        break;
      }
      sc = sc.parentElement;
    }
    setClipTop(scrollContainerTop);

    const spaceAbove = rect.top - scrollContainerTop - gap;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const openUpward = spaceBelow < maxHeight && spaceAbove > spaceBelow;
    const availableHeight = openUpward ? spaceAbove : spaceBelow;

    setDropdownStyle({
      position: 'fixed',
      ...(openUpward ? { bottom: window.innerHeight - rect.top + gap } : { top: rect.bottom + gap }),
      right: window.innerWidth - rect.right,
      minWidth: Math.max(rect.width, minWidth),
      maxHeight: Math.max(Math.min(maxHeight, availableHeight), 0),
      zIndex: 9999,
    });
  }, []);

  const handleOpen = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        triggerRef.current.scrollIntoView({ block: 'nearest' });
      }

      updateDropdownPosition();

      const themeAncestor = triggerRef.current.closest('[data-theme]');
      const modeAncestor = triggerRef.current.closest('[data-mode]') ?? document.documentElement;

      setPortalTheme({
        theme: themeAncestor?.getAttribute('data-theme') ?? 'core',
        mode: modeAncestor.getAttribute('data-mode') ?? 'light',
      });
    }

    setIsOpen(true);
  }, [updateDropdownPosition]);

  const handleOptionClick = useCallback(
    (value: string, flagProducing: boolean) => {
      if (flagProducing) {
        onToggleFlagOption(inputKey, value);
      } else {
        onSelectTerminal(inputKey, value);
      }
      handleClose();
      triggerRef.current?.focus();
    },
    [onToggleFlagOption, onSelectTerminal, inputKey, handleClose],
  );

  const handleOptionAction = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const { value, flagProducing } = e.currentTarget.dataset;
      if (!value) return;
      handleOptionClick(value, flagProducing === 'true');
    },
    [handleOptionClick],
  );

  const handleChipAction = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const { persisted, value, flagId } = e.currentTarget.dataset;
      if (!value) return;

      if (persisted === 'true') {
        if (!flagId) return;
        onRemoveActiveFlag?.(inputKey, flagId);
        return;
      }

      onToggleFlagOption(inputKey, value);
      // These chips render in the trigger regardless of open/closed state,
      // so a deselect can happen mid-session while the dropdown is still
      // open — in that case, defer to the eventual close (consistent with
      // toggling via the option list) rather than flushing early and
      // splitting one open/close session into two PATCHes. Only flush
      // immediately when it's a standalone action taken while already closed.
      if (!isOpen) onDraftFlush?.();
    },
    [inputKey, isOpen, onDraftFlush, onRemoveActiveFlag, onToggleFlagOption],
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleMouseDown = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return;
      if (dropdownRef.current?.contains(e.target as Node)) return;
      handleClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen, handleClose]);

  // Keep the portaled dropdown anchored to the trigger while it's open. Capture phase
  // covers scrollable ancestors; the options list's own internal scroll is harmless
  // since the trigger rect stays unchanged.
  useEffect(() => {
    if (!isOpen) return undefined;

    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);

    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return;
    const firstOption = dropdownRef.current.querySelector<HTMLButtonElement>('button');
    firstOption?.focus();
  }, [isOpen]);

  const handleDropdownKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
        triggerRef.current?.focus();
        return;
      }

      const optionButtons = Array.from(dropdownRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []);
      if (optionButtons.length === 0) return;
      const currentIndex = optionButtons.indexOf(document.activeElement as HTMLButtonElement);

      if (e.key === 'Tab') {
        e.preventDefault();
        handleClose();
        triggerRef.current?.focus();
        return;
      }

      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      if (e.key === 'ArrowDown') {
        optionButtons[Math.min(currentIndex + 1, optionButtons.length - 1)]?.focus();
      } else {
        optionButtons[Math.max(currentIndex - 1, 0)]?.focus();
      }
    },
    [handleClose],
  );

  const portalStyle = useMemo<React.CSSProperties>(
    () => ({
      position: 'fixed',
      top: clipTop,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
      zIndex: 9999,
    }),
    [clipTop],
  );

  const dropdownInlineStyle = useMemo<React.CSSProperties>(
    () => ({
      position: 'absolute',
      ...(dropdownStyle.top !== undefined
        ? { top: Number(dropdownStyle.top) - clipTop }
        : { bottom: dropdownStyle.bottom }),
      right: dropdownStyle.right,
      minWidth: dropdownStyle.minWidth,
      maxHeight: dropdownStyle.maxHeight,
      pointerEvents: 'auto',
    }),
    [
      clipTop,
      dropdownStyle.top,
      dropdownStyle.bottom,
      dropdownStyle.right,
      dropdownStyle.minWidth,
      dropdownStyle.maxHeight,
    ],
  );

  return (
    <div className="flex items-center justify-between border-b border-border-default py-6">
      <div className="flex items-center gap-4">
        <span className="body-bold-300 text-text-primary">{label}</span>
        {tooltipTrigger}
      </div>

      <div data-testid={`requirement-trigger-${inputKey}`} className="flex items-center gap-4">
        {hasChips ? (
          <div className="flex flex-wrap items-center justify-end gap-4">
            {chips.map((chip) => {
              const colorClass = flagPurposeTypeStyles[chip.option?.decisionType ?? 'none'];
              const chipLabel = chip.option?.label ?? chip.value;
              return (
                <span
                  key={chip.key}
                  className={`inline-flex items-center gap-4 rounded-10 px-10 py-6 text-[12px] font-bold ${colorClass}`}
                >
                  {chipLabel}
                  {!readOnly && (
                    <button
                      type="button"
                      aria-label={chip.persisted ? `Remove ${chipLabel}` : `Deselect ${chipLabel}`}
                      data-persisted={chip.persisted ? 'true' : 'false'}
                      data-value={chip.value}
                      data-flag-id={chip.flagId}
                      onClick={handleChipAction}
                      className="inline-flex items-center"
                    >
                      <PalmettoIcon name="x" size="xs" />
                    </button>
                  )}
                </span>
              );
            })}
            {!readOnly && (
              <button
                ref={triggerRef}
                type="button"
                onClick={handleOpen}
                aria-label="Add decision"
                data-decision-trigger="true"
                className="flex items-center gap-4 rounded-10 border-2 border-text-info bg-transparent px-8 py-8 text-text-info!"
              >
                <PalmettoIcon name="chevronDown" size="xs" />
              </button>
            )}
          </div>
        ) : (
          <button
            ref={triggerRef}
            type="button"
            disabled={readOnly}
            onClick={handleOpen}
            data-decision-trigger="true"
            className={`flex items-center gap-4 rounded-10 px-12 py-10 text-[12px] font-bold disabled:cursor-not-allowed disabled:opacity-60 ${triggerStyle}`}
          >
            <span>{terminalOption?.label ?? 'Decision'}</span>
            <PalmettoIcon name="chevronDown" size="xs" />
          </button>
        )}
      </div>

      {isOpen &&
        !readOnly &&
        createPortal(
          // Clip wrapper: a fixed layer that starts at the scroll container's top edge and
          // extends to the viewport bottom. overflow:hidden clips the absolutely-positioned
          // dropdown panel when the trigger scrolls behind a fixed header (navbar, AI
          // Assistant bar) — the dropdown stays open but disappears behind the header
          // rather than floating over it. pointer-events:none lets clicks pass through
          // the empty parts of the wrapper; the panel itself overrides to auto.
          // Re-establishes DSM theme tokens (data-mode/data-theme) lost when the portal
          // escapes the checklist's [data-theme] subtree.
          <div data-mode={portalTheme.mode} style={portalStyle}>
            <div
              ref={dropdownRef}
              data-theme={portalTheme.theme}
              style={dropdownInlineStyle}
              onKeyDown={handleDropdownKeyDown}
              className="overflow-y-auto rounded-16 border border-border-subtle bg-canvas-default shadow-md"
            >
              {options.map((opt) => {
                const colorClass = flagPurposeTypeStyles[opt.decisionType ?? 'none'];
                const flagProducing = isFlagProducingOption(opt);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    data-value={opt.value}
                    data-flag-producing={flagProducing ? 'true' : 'false'}
                    onClick={handleOptionAction}
                    className="flex w-full items-center px-12 py-8 hover:bg-canvas-subtle"
                  >
                    <span
                      className={`inline-flex w-full items-center justify-center rounded-10 px-12 py-8 text-[12px] font-bold ${colorClass}`}
                    >
                      {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export const ChecklistItemInputs: React.FC<ChecklistItemInputsProps> = ({
  inputs,
  data,
  onChange,
  onEvaluationChange,
  readOnly,
  itemKey,
  activeFlagsByInput,
  onRemoveActiveFlag,
  onFlagSelectionsChange,
  onPendingFlagSelectionsChange,
  onDraftFlush,
}) => {
  // Locally-toggled (unsaved) flag-producing selections, keyed by input key. Never mixed
  // into `data` — cleared for an input the moment a terminal option is picked on it, and
  // left untouched by persisted active flags (those live in `activeFlagsByInput`, owned by
  // the caller). Seeded from any persisted ChecklistPendingFlagDecision[] draft in `data` so
  // a rejection/stipulation selection re-shows as selected across an unmount/remount (e.g.
  // navigating away and back) without ever having opened the flag wizard.
  const [selectedFlagValuesByInput, setSelectedFlagValuesByInput] = useState<Record<string, string[]>>(() => {
    const seeded: Record<string, string[]> = {};
    for (const [key, value] of Object.entries(data)) {
      if (Array.isArray(value)) seeded[key] = value.map((decision) => decision.optionValue);
    }
    return seeded;
  });

  const handleChange = useCallback(
    (key: string, value: string | boolean) => {
      // Strip out any pending-flag-decision drafts before merging — a terminal value must
      // never fold an array back into the string|boolean-only `onChange` contract.
      const terminalData: Record<string, string | boolean> = {};
      for (const [dataKey, dataValue] of Object.entries(data)) {
        if (!Array.isArray(dataValue)) terminalData[dataKey] = dataValue;
      }
      onChange({ ...terminalData, [key]: value });
    },
    [data, onChange],
  );

  const handleToggleFlagOption = useCallback(
    (key: string, value: string) => {
      setSelectedFlagValuesByInput((prev) => {
        const current = prev[key] ?? [];
        const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
        onFlagSelectionsChange?.(key, next);
        if (onPendingFlagSelectionsChange) {
          const options = inputs?.find((input) => input.key === key)?.options;
          const decisions: ChecklistPendingFlagDecision[] = next.map((optionValue) => {
            const option = options?.find((o) => o.value === optionValue);
            return {
              optionValue,
              purposeType: option?.decisionType as FlagPurposeType,
              requirementFlagSettingId: option?.requirementFlagSettingId,
            };
          });
          onPendingFlagSelectionsChange(key, decisions);
        }
        return { ...prev, [key]: next };
      });
    },
    [onFlagSelectionsChange, onPendingFlagSelectionsChange, inputs],
  );

  const handleSelectTerminal = useCallback(
    (key: string, value: string) => {
      setSelectedFlagValuesByInput((prev) => (prev[key]?.length ? { ...prev, [key]: [] } : prev));
      handleChange(key, value);
      // Choosing a non-flagged decision resolves every active flag already on this input —
      // reuse the existing per-flag-id removal path (the chip's own "x" button) rather than
      // inventing a batch primitive. A no-op when the input has no active flags (e.g. only
      // pending/unsaved flag selections, which are cleared locally above instead).
      const activeFlags = activeFlagsByInput?.[key];
      if (activeFlags?.length) {
        for (const flag of activeFlags) onRemoveActiveFlag?.(key, flag.flagId);
      }
    },
    [handleChange, activeFlagsByInput, onRemoveActiveFlag],
  );

  const handleTextInputChange = useCallback(
    (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(key, e.target.value);
    },
    [handleChange],
  );

  const handleEvaluationChange = useCallback(
    (key: string) => (state: EvaluationState) => {
      onEvaluationChange?.(key, state);

      if (state.verdict === 'fail') {
        const currentTerminal = typeof data[key] === 'string' ? data[key] : '';
        const currentFlagValues = selectedFlagValuesByInput[key] ?? [];
        if (!currentTerminal && currentFlagValues.length === 0) {
          const inputDef = inputs?.find((input) => input.key === key);
          const core = getEvaluationCore(state.key);
          const autoOption = inputDef?.options?.find((o) => o.decisionType === core?.flagPurposeType);
          if (autoOption) handleToggleFlagOption(key, autoOption.value);
        }
      }
    },
    [onEvaluationChange, data, selectedFlagValuesByInput, inputs, handleToggleFlagOption],
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const handleContainerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Enter') return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'TEXTAREA') return;
      if (target.tagName !== 'INPUT') return;
      if (readOnly) return;
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;

      const allTriggers = Array.from(
        container.querySelectorAll<HTMLButtonElement>('[data-decision-trigger]:not([disabled])'),
      );
      const nextTrigger = allTriggers.find(
        (trigger) => !!(target.compareDocumentPosition(trigger) & Node.DOCUMENT_POSITION_FOLLOWING),
      );

      const allInputs = Array.from(container.querySelectorAll<HTMLInputElement>('input:not([disabled])'));
      const currentIndex = allInputs.indexOf(target as HTMLInputElement);
      if (currentIndex === -1) return;

      const nextInGroup = allInputs.slice(currentIndex + 1).find((input) => {
        if (!nextTrigger) return true;
        return !!(nextTrigger.compareDocumentPosition(input) & Node.DOCUMENT_POSITION_PRECEDING);
      });

      if (nextInGroup) {
        nextInGroup.focus();
      } else if (nextTrigger) {
        nextTrigger.click();
      } else {
        allInputs[0]?.focus();
      }
    },
    [readOnly],
  );

  if (!inputs || inputs.length === 0) return null;

  return (
    <div ref={containerRef} className="flex flex-col gap-8 pl-10" onKeyDown={handleContainerKeyDown}>
      {inputs.map((input) => {
        // A ChecklistPendingFlagDecision[] draft at this key is not a terminal value — it
        // means "flag-producing options are selected", surfaced instead via
        // `selectedFlagValuesByInput` below. Treat anything but a string as unset here.
        const currentValue = data[input.key];
        const terminalValue = typeof currentValue === 'string' ? currentValue : '';

        const tooltipTrigger =
          input.tooltipText && input.tooltipText.length > 0 ? (
            <InputTooltipTrigger tooltipId={`${itemKey ?? ''}:${input.key}`} tooltipText={input.tooltipText} />
          ) : undefined;

        switch (input.type) {
          case ChecklistItemInputType.text:
            if (readOnly) {
              return (
                <div key={input.key} className="flex flex-col gap-2 py-4">
                  <div className="flex items-center gap-4">
                    <span className="body-bold-300 text-text-primary">{input.label}</span>
                    {tooltipTrigger}
                  </div>
                  <span
                    aria-label={input.label}
                    className="py-1 h-8 w-[125px] rounded-16 border-2 border-border-default bg-input-bg-read-only px-4 body-300 text-text-secondary"
                  >
                    {terminalValue}
                  </span>
                </div>
              );
            }
            return (
              <InputWithTooltipSlot key={input.key} tooltipTrigger={tooltipTrigger}>
                <PalmettoTextInput
                  id={`checklist-input-${input.key}`}
                  label={input.label}
                  value={terminalValue}
                  onChange={handleTextInputChange(input.key)}
                  onBlur={onDraftFlush}
                  required={input.required}
                  disabled={readOnly}
                />
              </InputWithTooltipSlot>
            );

          case ChecklistItemInputType.requirement:
            return (
              <RequirementSelect
                key={input.key}
                inputKey={input.key}
                label={input.label}
                options={input.options}
                terminalValue={terminalValue}
                selectedFlagValues={selectedFlagValuesByInput[input.key] ?? EMPTY_FLAG_VALUES}
                activeFlags={activeFlagsByInput?.[input.key] ?? EMPTY_ACTIVE_FLAGS}
                readOnly={readOnly}
                onToggleFlagOption={handleToggleFlagOption}
                onSelectTerminal={handleSelectTerminal}
                onRemoveActiveFlag={readOnly ? undefined : onRemoveActiveFlag}
                onDraftFlush={onDraftFlush}
                tooltipTrigger={tooltipTrigger}
              />
            );

          case ChecklistItemInputType.referenceValue: {
            const view = input;
            return (
              <div key={input.key} className="flex flex-col gap-2 py-4">
                <div className="flex items-center gap-4">
                  <span className="body-bold-300 text-text-primary">{input.label}</span>
                  {tooltipTrigger}
                </div>
                <span className="body-300 text-text-secondary">{view.resolvedValue ?? '—'}</span>
              </div>
            );
          }

          case ChecklistItemInputType.evaluation: {
            if (!input.evaluationKey) return null;
            const descriptor = evaluationClientRegistry[input.evaluationKey];
            if (!descriptor) return null;
            const { Component } = descriptor;
            const params: EvaluationParams =
              input.evaluation?.params ?? input.evaluationParams ?? EMPTY_EVALUATION_PARAMS;
            const shouldShowDecision =
              !!(input.options && input.options.length > 0) && input.evaluation?.hasRequiredData !== false;
            return (
              <div key={input.key} className="flex flex-col gap-8">
                <InputWithTooltipSlot tooltipTrigger={tooltipTrigger} anchorTop>
                  <Component
                    key={input.key}
                    inputKey={input.key}
                    label={input.label}
                    view={input.evaluation}
                    params={params}
                    readOnly={readOnly}
                    onChange={handleEvaluationChange(input.key)}
                    itemKey={itemKey}
                  />
                </InputWithTooltipSlot>
                {shouldShowDecision && (
                  <RequirementSelect
                    inputKey={input.key}
                    label="Decision"
                    options={input.options}
                    terminalValue={terminalValue}
                    selectedFlagValues={selectedFlagValuesByInput[input.key] ?? EMPTY_FLAG_VALUES}
                    activeFlags={activeFlagsByInput?.[input.key] ?? EMPTY_ACTIVE_FLAGS}
                    readOnly={readOnly}
                    onToggleFlagOption={handleToggleFlagOption}
                    onSelectTerminal={handleSelectTerminal}
                    onRemoveActiveFlag={readOnly ? undefined : onRemoveActiveFlag}
                    onDraftFlush={onDraftFlush}
                  />
                )}
              </div>
            );
          }

          default:
            return null;
        }
      })}
    </div>
  );
};
